// /client/src/components/MessageLog.js
// Message Log with Journey Timeline

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Navigation from './Navigation';

const MessageLog = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({ sent: 0, queued: 0 });
  const [expandedRows, setExpandedRows] = useState({});

  // Date range helpers
  const getDateRange = (offsetWeeks = 0) => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - (offsetWeeks * 7) + 7);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 13);
    return {
      dateFrom: startDate.toISOString().split('T')[0],
      dateTo: endDate.toISOString().split('T')[0]
    };
  };

  const [weekOffset, setWeekOffset] = useState(0);
  const [filters, setFilters] = useState({
    direction: '',
    messageType: '',
    status: '',
    ...getDateRange(0)
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  const directionOptions = ['inbound', 'outbound'];
  const messageTypeOptions = [
    'opt-in-request', 'opt-in-confirmation', 'opt-out-confirmation',
    'help-request', 'help-response', 'info-request',
    'campaign', 'pod-relay', 'test', 'sms', 'system'
  ];
  const statusOptions = ['scheduled', 'queued', 'sent', 'delivered', 'failed', 'received'];

  const isHelpRequest = (msg) => {
    return msg.messageType === 'help-request' || 
           msg.metadata?.isHelpRequest === true ||
           (msg.body && msg.body.trim().toUpperCase() === 'HELP');
  };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({ limit: 500, includeQueued: 'true' });
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.messageType) params.append('messageType', filters.messageType);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await fetch(`/api/messagelogs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessages(data.messages || []);
          setCounts(data.counts || { sent: 0, queued: 0 });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch messages');
      }
    } catch (err) {
      setError('Failed to load messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Navigation and filter handlers
  const goToPreviousWeek = () => {
    const newOffset = weekOffset + 1;
    setWeekOffset(newOffset);
    setFilters(prev => ({ ...prev, ...getDateRange(newOffset) }));
  };

  const goToNextWeek = () => {
    if (weekOffset > 0) {
      const newOffset = weekOffset - 1;
      setWeekOffset(newOffset);
      setFilters(prev => ({ ...prev, ...getDateRange(newOffset) }));
    }
  };

  const goToCurrentWeek = () => {
    setWeekOffset(0);
    setFilters(prev => ({ ...prev, ...getDateRange(0) }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setWeekOffset(0);
    setFilters({ direction: '', messageType: '', status: '', ...getDateRange(0) });
  };

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedMessages = useMemo(() => {
    let sorted = [...messages];
    if (sortConfig !== null) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'scheduledFor') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [messages, sortConfig]);

  // Counts
  const helpRequestCount = messages.filter(isHelpRequest).length;
  const scheduledCount = messages.filter(m => m.isQueued).length;
  const failedCount = messages.filter(m => m.status === 'failed').length;

  const statusCounts = useMemo(() => {
    const counts = {};
    messages.forEach(msg => {
      const status = msg.isQueued ? 'scheduled' : msg.status;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [messages]);

  // Formatters
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatDateRange = () => {
    const from = new Date(filters.dateFrom);
    const to = new Date(filters.dateTo);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${from.toLocaleDateString('en-US', options)} – ${to.toLocaleDateString('en-US', options)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'is-link', queued: 'is-warning', accepted: 'is-info is-light',
      sending: 'is-info', sent: 'is-info', delivered: 'is-success',
      undelivered: 'is-danger is-light', failed: 'is-danger', received: 'is-primary'
    };
    return colors[status] || 'is-light';
  };

  const getJourneyStatusColor = (status) => {
    const colors = {
      scheduled: '#3273dc', queued: '#ffdd57', processing: '#ffdd57',
      accepted: '#3298dc', sending: '#3298dc', sent: '#3298dc',
      delivered: '#48c774', undelivered: '#f14668', failed: '#f14668',
      receiving: '#00d1b2', received: '#00d1b2', read: '#48c774',
      cancelled: '#363636'
    };
    return colors[status] || '#888';
  };

  const getDirectionLabel = (direction) => direction === 'inbound' ? '↓ Received' : '↑ Sent';
  const getDirectionColor = (direction) => direction === 'inbound' ? 'is-info' : 'is-primary';

  // Determine if status is from Twilio or our App
  const isTwilioStatus = (status) => {
    const twilioStatuses = ['accepted', 'sending', 'sent', 'delivered', 'undelivered', 'failed', 'read', 'receiving', 'received'];
    return twilioStatuses.includes(status);
  };

  const isAppStatus = (status) => {
    const appStatuses = ['scheduled', 'queued', 'processing', 'cancelled'];
    return appStatuses.includes(status);
  };

  const getMessageTypeLabel = (type) => {
    const labels = {
      'opt-in-request': 'Opt-in Request', 'opt-in-confirmation': 'Opt-in Confirm',
      'opt-out-confirmation': 'Opt-out Confirm', 'help-request': '🆘 HELP',
      'help-response': 'Help Response', 'info-request': 'Info Request',
      'campaign': 'Campaign', 'pod-relay': 'Pod Relay',
      'test': 'Test', 'sms': 'SMS', 'system': 'System'
    };
    return labels[type] || type || 'Unknown';
  };

  const getSortIcon = (columnName) => {
    const style = { width: '12px', height: '12px', marginLeft: '8px', display: 'inline-block' };
    if (sortConfig.key !== columnName) {
      return <svg style={style} viewBox="0 0 24 24" fill="currentColor"><path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/></svg>;
    }
    if (sortConfig.direction === 'ascending') {
      return <svg style={style} viewBox="0 0 24 24" fill="currentColor"><path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/></svg>;
    }
    return <svg style={style} viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/></svg>;
  };

  const hasActiveFilters = filters.direction || filters.messageType || filters.status;

  // Journey Timeline Component
  const JourneyTimeline = ({ journey }) => {
    if (!journey || journey.length === 0) return <span className="has-text-grey is-size-7">No data</span>;

    return (
      <div className="journey-timeline" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        {journey.map((step, idx) => {
          const fromTwilio = isTwilioStatus(step.status);
          const fromApp = isAppStatus(step.status);
          
          return (
            <React.Fragment key={idx}>
              <div
                title={`${step.label}: ${formatDateTime(step.timestamp)}${step.note ? ` - ${step.note}` : ''} (${fromTwilio ? 'Twilio' : 'App'})`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  backgroundColor: getJourneyStatusColor(step.status),
                  color: ['queued', 'processing'].includes(step.status) ? '#333' : '#fff',
                  fontSize: '0.7rem',
                  fontWeight: '500',
                  cursor: 'help',
                  border: fromTwilio ? '2px solid #f14668' : '2px dashed #3273dc',
                  boxSizing: 'border-box'
                }}
              >
                <span style={{ fontSize: '0.6rem', opacity: 0.9 }}>{fromTwilio ? '📱' : '⚙️'}</span>
                <span>{step.label}</span>
                <span style={{ opacity: 0.8, fontSize: '0.65rem' }}>
                  {new Date(step.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </span>
              </div>
              {idx < journey.length - 1 && (
                <span style={{ color: '#ccc', fontSize: '0.7rem' }}>›</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (loading && messages.length === 0) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <div className="has-text-centered py-6">
              <div className="is-loading"></div>
              <p>Loading message log...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            {/* Header */}
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <div className="is-flex is-align-items-center">
                <h1 className='h1 my-2'>Message Log</h1>
                {scheduledCount > 0 && (
                  <span className="tag is-link is-medium ml-3" style={{ cursor: 'pointer' }}
                    onClick={() => setFilters(prev => ({ ...prev, status: 'scheduled' }))}>
                    ⏱️ {scheduledCount} Scheduled
                  </span>
                )}
                {helpRequestCount > 0 && (
                  <span className="tag is-danger is-medium ml-2" 
                    style={{ animation: 'pulse 2s infinite', cursor: 'pointer' }}
                    onClick={() => setFilters(prev => ({ ...prev, messageType: 'help-request' }))}>
                    🆘 {helpRequestCount} Help
                  </span>
                )}
              </div>
              <button className="button is-light" onClick={fetchMessages} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Date Range */}
            <div className="mb-4 has-text-centered">
              <p className="is-size-5 has-text-weight-semibold">{formatDateRange()}</p>
              <p className="is-size-7 has-text-grey">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
                {counts.queued > 0 && ` (${counts.queued} scheduled)`}
              </p>
            </div>

            {/* Status Cards */}
            {Object.keys(statusCounts).length > 0 && (
              <div className="columns is-multiline mb-4">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div className="column is-2" key={status}>
                    <div 
                      className={`box has-text-centered p-3 ${filters.status === status ? 'has-background-light' : ''}`}
                      style={{ cursor: 'pointer', border: filters.status === status ? '2px solid #3273dc' : '1px solid #dbdbdb' }}
                      onClick={() => setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status }))}
                    >
                      <p className="is-size-7 has-text-grey mb-1" style={{ textTransform: 'capitalize' }}>{status}</p>
                      <p className={`is-size-4 has-text-weight-bold ${
                        status === 'failed' ? 'has-text-danger' :
                        status === 'delivered' ? 'has-text-success' :
                        status === 'scheduled' ? 'has-text-link' : 'has-text-dark'
                      }`}>{count}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Alerts */}
            {helpRequestCount > 0 && filters.messageType !== 'help-request' && (
              <div className="notification is-danger is-light mb-4" style={{ borderLeft: '4px solid #f14668' }}>
                <div className="is-flex is-justify-content-space-between is-align-items-center">
                  <div><strong>🆘 Attention:</strong> {helpRequestCount} help request{helpRequestCount !== 1 ? 's' : ''}</div>
                  <button className="button is-danger is-small" 
                    onClick={() => setFilters(prev => ({ ...prev, messageType: 'help-request' }))}>View</button>
                </div>
              </div>
            )}

            {failedCount > 0 && filters.status !== 'failed' && (
              <div className="notification is-warning is-light mb-4" style={{ borderLeft: '4px solid #ffdd57' }}>
                <div className="is-flex is-justify-content-space-between is-align-items-center">
                  <div><strong>⚠️ Issues:</strong> {failedCount} failed</div>
                  <button className="button is-warning is-small"
                    onClick={() => setFilters(prev => ({ ...prev, status: 'failed' }))}>View</button>
                </div>
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button className="delete" onClick={() => setError('')}></button>
                {error}
                <button className="button is-small is-light mt-2" onClick={fetchMessages}>Retry</button>
              </div>
            )}

            {/* Filters */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">Direction</label>
                    <div className="select is-fullwidth">
                      <select name="direction" value={filters.direction} onChange={handleFilterChange}>
                        <option value="">All</option>
                        {directionOptions.map(d => <option key={d} value={d}>{getDirectionLabel(d)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">Type</label>
                    <div className="select is-fullwidth">
                      <select name="messageType" value={filters.messageType} onChange={handleFilterChange}>
                        <option value="">All</option>
                        {messageTypeOptions.map(t => <option key={t} value={t}>{getMessageTypeLabel(t)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">Status</label>
                    <div className="select is-fullwidth">
                      <select name="status" value={filters.status} onChange={handleFilterChange}>
                        <option value="">All</option>
                        {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">From</label>
                    <input type="date" className="input" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
                  </div>
                </div>
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">To</label>
                    <input type="date" className="input" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
                  </div>
                </div>
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <button className="button is-light is-fullwidth" onClick={clearFilters} 
                      disabled={!hasActiveFilters && weekOffset === 0}>Reset</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            {sortedMessages.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>{hasActiveFilters ? 'No messages match filters.' : 'No messages found.'}</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th style={{ width: '30px' }}></th>
                        <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                          Date {getSortIcon('createdAt')}
                        </th>
                        <th onClick={() => handleSort('direction')} style={{ cursor: 'pointer' }}>
                          Dir {getSortIcon('direction')}
                        </th>
                        <th>Contact</th>
                        <th onClick={() => handleSort('messageType')} style={{ cursor: 'pointer' }}>
                          Type {getSortIcon('messageType')}
                        </th>
                        <th>Journey</th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                          Status {getSortIcon('status')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMessages.map((msg) => {
                        const isHelp = isHelpRequest(msg);
                        const isScheduled = msg.isQueued;
                        const isFailed = msg.status === 'failed';
                        const isExpanded = expandedRows[msg._id];

                        return (
                          <React.Fragment key={msg._id}>
                            <tr style={{
                              backgroundColor: isHelp ? '#fff5f5' : isScheduled ? '#eff5fb' : isFailed ? '#fffbeb' : 'transparent',
                              borderLeft: isHelp ? '4px solid #f14668' : isScheduled ? '4px solid #3273dc' : isFailed ? '4px solid #ffdd57' : 'none',
                              cursor: 'pointer'
                            }} onClick={() => toggleRowExpand(msg._id)}>
                              <td className="is-size-7">
                                <span style={{ color: '#888' }}>{isExpanded ? '▼' : '▶'}</span>
                              </td>
                              <td className="is-size-7">
                                {isScheduled && <span title="Scheduled">⏱️ </span>}
                                {isScheduled ? formatDateTime(msg.scheduledFor) : formatDateTime(msg.createdAt)}
                              </td>
                              <td>
                                <span className={`tag is-small ${getDirectionColor(msg.direction)}`}>
                                  {msg.direction === 'inbound' ? '↓ Received' : '↑ Sent'}
                                </span>
                              </td>
                              <td className="is-size-7">
                                {msg.contactName || <code>{msg.to?.slice(-4)?.padStart(msg.to?.length || 4, '•')}</code>}
                              </td>
                              <td className="is-size-7">{getMessageTypeLabel(msg.messageType)}</td>
                              <td><JourneyTimeline journey={msg.journey} /></td>
                              <td>
                                <span className={`tag is-small ${getStatusColor(isScheduled ? 'scheduled' : msg.status)}`}>
                                  {isScheduled ? 'Scheduled' : (msg.status?.charAt(0).toUpperCase() + msg.status?.slice(1))}
                                </span>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan="7" style={{ backgroundColor: '#fafafa', padding: '1rem' }}>
                                  <div className="columns">
                                    <div className="column is-6">
                                      <p className="is-size-7"><strong>To:</strong> <code>{msg.to}</code></p>
                                      <p className="is-size-7"><strong>Preview:</strong> {msg.bodyPreview || '[No content]'}</p>
                                      {msg.errorCode && (
                                        <p className="is-size-7 has-text-danger">
                                          <strong>Error:</strong> {msg.errorCode} - {msg.errorMessage}
                                        </p>
                                      )}
                                    </div>
                                    <div className="column is-6">
                                      <p className="is-size-7 has-text-weight-semibold mb-2">Full Journey:</p>
                                      {msg.journey?.map((step, idx) => (
                                        <p key={idx} className="is-size-7" style={{ marginLeft: '1rem' }}>
                                          <span style={{ 
                                            display: 'inline-block',
                                            width: '18px',
                                            fontWeight: '600',
                                            color: '#666'
                                          }}>{idx + 1}.</span>
                                          <span style={{ 
                                            display: 'inline-block', width: '8px', height: '8px', 
                                            borderRadius: '50%', backgroundColor: getJourneyStatusColor(step.status),
                                            marginRight: '8px'
                                          }}></span>
                                          <strong>{step.label}:</strong> {formatDateTime(step.timestamp)}
                                          {step.note && <span className="has-text-grey"> ({step.note})</span>}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Navigation */}
                <div className="is-flex is-justify-content-center is-align-items-center mt-5 mb-3">
                  <button className="button is-medium mr-3" onClick={goToPreviousWeek} disabled={loading}>
                    ← Back 7 Days
                  </button>
                  {weekOffset > 0 && (
                    <button className="button is-light is-medium mr-3" onClick={goToCurrentWeek} disabled={loading}>
                      Current
                    </button>
                  )}
                  <button className="button is-medium" onClick={goToNextWeek} disabled={loading || weekOffset === 0}>
                    Next 7 Days →
                  </button>
                </div>

                {/* Legend */}
                <div className="has-text-centered mb-5">
                  <p className="is-size-7 has-text-grey">
                    <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '16px' }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        border: '2px dashed #3273dc', 
                        borderRadius: '4px',
                        marginRight: '4px'
                      }}></span>
                      ⚙️ App Status
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid #f14668', 
                        borderRadius: '4px',
                        marginRight: '4px'
                      }}></span>
                      📱 Twilio Status
                    </span>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default MessageLog;